import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Select, MenuItem, FormControl, InputLabel,
  InputAdornment, IconButton, Snackbar, Alert, Paper, SelectChangeEvent,
  CircularProgress, Typography, LinearProgress, useTheme
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle as SuccessIcon, Error as ErrorIcon, Warning as WarningIcon, Close as CloseIcon } from '@mui/icons-material';
import axios from 'axios';

interface CreateAccountProps {
  username: string;
  currentRole: string;
  loading: boolean;
}

interface Region {
  id: number;
  name: string;
}

interface DocumentCreationStatus {
  isRunning: boolean;
  success?: boolean;
  error?: boolean;
  message: string;
  fileName?: string;
  skipped?: boolean;
}

const CreateAccount: React.FC<CreateAccountProps> = ({ currentRole, loading }) => {
  const theme = useTheme();
  const [createUser, setCreateUser] = useState('');
  const [password, setPassword] = useState('ZerdePh');
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState('user');
  const [pharmacyNum, setPharmacyNum] = useState('');
  const [viewerAccess, setViewerAccess] = useState('');
  const [cityId, setCityId] = useState('');
  const [createUserError, setCreateUserError] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [regions, setRegions] = useState<Region[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  // Document creation status
  const [docStatus, setDocStatus] = useState<DocumentCreationStatus | null>(null);
  const [showDocIndicator, setShowDocIndicator] = useState(false);
  const [isIndicatorHovered, setIsIndicatorHovered] = useState(false);

  const isInterfaceBlocked = loading || !currentRole || currentRole === 'NaN' || currentRole === 'undefined';

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const checkCreateUser = async () => {
    if (!createUser) return;

    try {
      const response = await axios.post('https://tbl.zerdegroup.kz/api/check-createUser', { createUser });
      if (response.data.exists) {
        setCreateUserError('Логин уже используется');
      } else {
        setCreateUserError('');
      }
    } catch (error) {
      console.error('Ошибка при проверке логина', error);
    }
  };

  const handleBlur = () => {
    checkCreateUser();
  };

  const logAction = async (action: string) => {
    const role = localStorage.getItem('role') || 'unknown';
    const username = localStorage.getItem('username') || 'unknown';
    try {
      await axios.post('https://tbl.zerdegroup.kz/api/log', { role, username, action });
    } catch (error) {
      console.error('Ошибка при логировании действия:', error);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (createUserError || isCreating) return;

    setIsCreating(true);

    // Show document creation indicator for 'user' role
    if (role === 'user') {
      setDocStatus({
        isRunning: true,
        message: `Создание пользователя и табельного документа для аптеки №${pharmacyNum}...`
      });
      setShowDocIndicator(true);
    }

    try {
      const response = await axios.post('https://tbl.zerdegroup.kz/api/create-user', {
        createUser,
        password,
        role,
        pharmacyNum,
        cityId: cityId || null,
        viewerAccess: role === 'view' ? viewerAccess : undefined
      });

      if (response.data.success) {
        // Базовое сообщение
        let message = response.data.message || 'Пользователь успешно создан';

        // Update document status for 'user' role
        if (role === 'user') {
          if (response.data.timesheetCreated) {
            setDocStatus({
              isRunning: false,
              success: true,
              message: 'Табельный документ успешно создан',
              fileName: response.data.timesheetInfo?.fileName
            });
          } else if (response.data.timesheetSkipped || response.data.timesheetInfo?.skipped) {
            setDocStatus({
              isRunning: false,
              success: true,
              skipped: true,
              message: 'Табельный документ уже существует'
            });
          } else {
            setDocStatus({
              isRunning: false,
              error: true,
              message: response.data.timesheetMessage || response.data.timesheetError || 'Ошибка создания документа'
            });
          }

          // Auto-hide after 10 seconds
          setTimeout(() => {
            setShowDocIndicator(false);
            setDocStatus(null);
          }, 10000);
        }

        // Дополнительная информация о табельном документе для роли 'user'
        if (role === 'user' && response.data.timesheetCreated !== undefined) {
          if (response.data.timesheetCreated) {
            // Табельный документ создан успешно
            if (response.data.timesheetInfo?.fileName) {
              message += `\n📄 Табельный документ "${response.data.timesheetInfo.fileName}" создан`;
            }
          } else {
            // Проблема с созданием табельного документа
            if (response.data.timesheetMessage) {
              message += `\n⚠️ ${response.data.timesheetMessage}`;
            } else if (response.data.timesheetError) {
              message += `\n Ошибка создания табеля: ${response.data.timesheetError}`;
            }
          }
        }

        showNotification(message, 'success');
        logAction(`создал пользователя "${createUser}" | Создать пользователя`);

        // Очистка формы
        setCreateUser('');
        setPassword('');
        setRole('user');
        setPharmacyNum('');
        setCityId('');
        setViewerAccess('');
      } else {
        if (role === 'user') {
          setDocStatus({
            isRunning: false,
            error: true,
            message: 'Ошибка при создании пользователя'
          });
        }
        showNotification('Ошибка при создании пользователя', 'error');
      }
    } catch (error: any) {
      let errorMessage = 'Произошла ошибка при создании пользователя';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }

      if (role === 'user') {
        setDocStatus({
          isRunning: false,
          error: true,
          message: errorMessage
        });
      }

      showNotification(errorMessage, 'error');
      console.error('Ошибка при создании пользователя:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleViewerAccessChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const formattedValue = value.replace(/\s+/g, ',');
    setViewerAccess(formattedValue);
  };

  const handleRoleChange = (e: SelectChangeEvent) => {
    const newRole = e.target.value;
    setRole(newRole);

    // Если выбрана роль "Аптека" (user), ставим пароль по умолчанию
    if (newRole === 'user') {
      setPassword('ZerdePh');
    } else {
      // Если переключили с аптеки - очищаем пароль, чтобы не остался "ZerdePh"
      if (password === 'ZerdePh') {
        setPassword('');
      }
    }

    // Для view, guide, moder, km, to - ставим "-"
    if (newRole === 'view' || newRole === 'guide' || newRole === 'moder' || newRole === 'km' || newRole === 'to' || newRole === 'full-report') {
      setPharmacyNum('-');
    } else if (newRole === 'user' || newRole === 'storage') {
      // Для user и storage - пустое поле (они будут вводить значение)
      setPharmacyNum('');
    }
  };

  useEffect(() => {
    const isValid = createUser !== '' &&
      password !== '' &&
      role !== '' &&
      pharmacyNum !== '' &&
      cityId !== '' &&
      !createUserError &&
      (role !== 'view' || (role === 'view' && viewerAccess !== ''));
    setIsFormValid(isValid);
  }, [createUser, password, role, pharmacyNum, createUserError, viewerAccess, cityId]);

  const fetchRegions = async () => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/regions');
      if (response.data.success) {
        setRegions(response.data.data);
      } else {
        showNotification('Ошибка при загрузке регионов', 'error');
      }
    } catch (error) {
      console.error('Ошибка при загрузке регионов:', error);
      showNotification('Ошибка при загрузке регионов', 'error');
    }
  };

  useEffect(() => {
    fetchRegions();
  }, []);

  // Floating Progress Indicator Component
  const FloatingDocIndicator = () => {
    if (!docStatus || !showDocIndicator) return null;

    const isRunning = docStatus.isRunning;
    const isSuccess = !isRunning && docStatus.success && !docStatus.error;
    const hasError = docStatus.error;
    const isSkipped = docStatus.skipped;

    // Define colors based on state
    const bgGradient = isRunning
      ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
      : isSuccess
        ? `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`
        : hasError
          ? `linear-gradient(135deg, ${theme.palette.error.dark} 0%, ${theme.palette.error.main} 100%)`
          : `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.main} 100%)`;

    return (
      <Paper
        elevation={8}
        onMouseEnter={() => setIsIndicatorHovered(true)}
        onMouseLeave={() => setIsIndicatorHovered(false)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1300,
          width: 320,
          overflow: 'hidden',
          borderRadius: 3,
          background: bgGradient,
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(255,255,255,0.1)',
          cursor: 'pointer'
        }}
      >
        {/* Header - Always Visible */}
        <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0
            }}>
              {isRunning ? (
                <CircularProgress size={18} thickness={5} sx={{ color: '#fff' }} />
              ) : isSuccess ? (
                <SuccessIcon sx={{ fontSize: 20 }} />
              ) : hasError ? (
                <ErrorIcon sx={{ fontSize: 20 }} />
              ) : (
                <WarningIcon sx={{ fontSize: 20 }} />
              )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.3, fontSize: 13 }}>
                {isRunning ? 'Создание документа' : (isSuccess ? (isSkipped ? 'Документ существует' : 'Готово!') : 'Ошибка')}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  opacity: 0.9,
                  display: 'block',
                  lineHeight: 1.3,
                  fontSize: 11,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: isIndicatorHovered ? 'normal' : 'nowrap'
                }}
              >
                {docStatus.message}
              </Typography>
            </Box>
          </Box>

          {!isRunning && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setShowDocIndicator(false);
                setDocStatus(null);
              }}
              sx={{
                color: 'rgba(255,255,255,0.7)',
                flexShrink: 0,
                '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Expandable Content - Shows on Hover */}
        {isIndicatorHovered && docStatus.fileName && (
          <Box sx={{ px: 2, pb: 2 }}>
            <Box sx={{ bgcolor: 'rgba(0,0,0,0.1)', p: 1.5, borderRadius: 2 }}>
              <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', fontSize: 10 }}>Файл</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {docStatus.fileName}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Progress indicator for running state */}
        {isRunning && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <LinearProgress
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.2)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#fff',
                  borderRadius: 2
                }
              }}
            />
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Floating Document Creation Indicator */}
      <FloatingDocIndicator />

      <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
        <TextField
          name="createUser"
          label="Логин"
          variant="outlined"
          margin="normal"
          value={createUser}
          onChange={(e) => setCreateUser(e.target.value)}
          onBlur={handleBlur}
          disabled={isInterfaceBlocked || localStorage.getItem('role') === 'view'}
          error={!!createUserError}
          helperText={createUserError || "Имя пользователя должно всегда начинаться с буквы!"}
          sx={{ width: '450px' }}
        />
        <TextField
          sx={{ width: '450px' }}
          name="password"
          label="Пароль"
          type={showPassword ? 'text' : 'password'}
          variant="outlined"
          margin="normal"
          value={password}
          disabled={isInterfaceBlocked || localStorage.getItem('role') === 'view' || role === 'user'}
          onChange={(e) => setPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  edge="end"
                  sx={{ color: 'darkgray' }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {(role === 'user' || role === 'storage') && (
        <TextField
          fullWidth
          label={role === 'storage' ? 'Наименование склада' : 'Номер аптеки'}
          variant="outlined"
          margin="normal"
          value={pharmacyNum}
          onChange={(e) => setPharmacyNum(e.target.value)}
          helperText={role === 'storage' ? 'Только латиница' : 'Только числа и знаки -_/'}
          disabled={isInterfaceBlocked || localStorage.getItem('role') === 'view'}
          sx={{ width: '450px' }}
        />
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <FormControl fullWidth margin="normal" sx={{ width: '450px' }}>
          <InputLabel>Роль</InputLabel>
          <Select
            value={role}
            label="Роль"
            onChange={handleRoleChange}
            disabled={localStorage.getItem('role') === 'moder' || localStorage.getItem('role') === 'view' || isInterfaceBlocked}
          >
            <MenuItem value="storage">Склад</MenuItem>
            <MenuItem value="user">Аптека</MenuItem>
            <MenuItem value="guide">Справочная</MenuItem>
            <MenuItem value="view">Просмотр</MenuItem>
            <MenuItem value="km">Отдел КМ</MenuItem>
            <MenuItem value="to">Торг. отдел</MenuItem>
            <MenuItem value="moder">Модератор</MenuItem>
            <MenuItem value="full-report">Доступ к отчетам</MenuItem>
          </Select>
        </FormControl>

        {role === 'view' && (
          <TextField
            sx={{ width: '450px' }}
            fullWidth
            label="Доступные аптеки"
            variant="outlined"
            margin="normal"
            value={viewerAccess}
            onChange={handleViewerAccessChange}
            helperText="Введите номера аптек через пробел или запятую (например: 301,23,1_7)"
            disabled={isInterfaceBlocked}
          />
        )}

        <FormControl fullWidth margin="normal" sx={{ width: '450px' }}>
          <InputLabel>Регион</InputLabel>
          <Select
            value={cityId}
            label="Регион"
            onChange={(e) => setCityId(e.target.value)}
            disabled={isInterfaceBlocked || localStorage.getItem('role') === 'view'}
          >
            {regions.map((region) => (
              <MenuItem key={region.id} value={region.name}>
                {region.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

      </Box>
      <Button
        type="submit"
        variant="contained"
        disabled={!isFormValid || isInterfaceBlocked || localStorage.getItem('role') === 'view' || isCreating}
        startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : null}
        sx={{
          width: 450,
          mt: 2,
          background: isCreating
            ? 'linear-gradient(45deg, #cccccc 30%, #999999 90%)'
            : 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
          transition: 'all 0.3s ease',
          '&:hover': {
            background: isCreating
              ? 'linear-gradient(45deg, #cccccc 30%, #999999 90%)'
              : 'linear-gradient(45deg, #FF8E53 30%, #FE6B8B 90%)'
          },
          '&:disabled': {
            background: 'linear-gradient(45deg, #cccccc 30%, #999999 90%)',
            color: '#666666'
          }
        }}
      >
        {isCreating ? 'Создание...' : 'Создать'}
      </Button>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.message.includes('Табельный документ') ? 10000 : 6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateAccount;